import { useReducer } from 'react';

import { Message } from '../components/chat-controls/ChatConversation';
import { Status } from '../domain/status';
import { NLWebResponse, MinimalNLSearchRequest } from '../models';

/**
 * The state of the chat history.
 *
 * @property messages - Messages in the chat.
 * @property status - Current loading state of the chat.
 */
type State = {
    messages: Message[];
    status: Status;
};

/**
 * Enum of actions that can be dispatched to the reducer.
 */
enum TargetAction {
    StatusChanged = 'statusChanged',
    QueryChanged = 'queryChanged',
    ResponseReceived = 'responseReceived',
    Reset = 'reset',
}

/**
 * Available actions to be dispatched to the reducer.
 *
 * @property type - The type of action to be dispatched.
 * @property payload - The payload of the action. The type of the payload is determined by the type of action.
 */
type Action =
    | { type: TargetAction.StatusChanged; payload: Status }
    | { type: TargetAction.QueryChanged; payload: MinimalNLSearchRequest }
    | { type: TargetAction.ResponseReceived; payload: NLWebResponse }
    | { type: TargetAction.Reset };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case TargetAction.StatusChanged: {
            return {
                ...state,
                status: action.payload,
            };
        }

        case TargetAction.QueryChanged: {
            const messages = [...state.messages];
            if (!action.payload.retry) {
                messages.push(action.payload);
            }
            return {
                ...state,
                messages,
                status: Status.Ready,
            };
        }

        case TargetAction.ResponseReceived: {
            const data: NLWebResponse = action.payload;
            const prevMessages = state.messages;
            return {
                ...state,
                messages: [...prevMessages, data],
                status: Status.Ready,
            };
        }

        case TargetAction.Reset: {
            return {
                ...state,
                messages: [],
                status: Status.Initial,
            };
        }

        // No-op for unknown actions.
        default:
            return state;
    }
};

/**
 * Hook to manage the chat history.
 *
 * @returns The current state of the chat history and the dispatch functions.
 */
export const useChatHistory = () => {
    const [state, dispatch] = useReducer(reducer, {
        messages: [],
        status: Status.Initial,
    });
    const { messages, status } = state;

    const dispatchStatus = (status: Status) => {
        dispatch({ type: TargetAction.StatusChanged, payload: status });
    };

    const dispatchQuery = (request: MinimalNLSearchRequest) => {
        dispatch({ type: TargetAction.QueryChanged, payload: request });
    };

    const dispatchResponse = (response: NLWebResponse) => {
        dispatch({ type: TargetAction.ResponseReceived, payload: response });
    };

    const dispatchReset = () => {
        dispatch({ type: TargetAction.Reset });
    };

    return {
        messages,
        status,
        state,
        dispatchStatus,
        dispatchQuery,
        dispatchResponse,
        dispatchReset,
    };
};
